import { useState, useRef } from 'react';
import { formatPrice } from '../../utils/format';
import { useEvents } from '../../context/EventsContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Upload, Image, X, Loader2, Check, AlertCircle } from 'lucide-react';

const ReservationForm = ({ event, chairs, tables, totalPrice, paymentMethods = [], onSuccess, onCancel }) => {
  const fileInputRef = useRef(null);
  const { createReservation } = useEvents();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const activeMethods = paymentMethods.filter(m => m.active);
  const [paymentMethod, setPaymentMethod] = useState(activeMethods[0]?.id?.toString() || '');
  const selectedMethod = activeMethods.find(m => m.id.toString() === paymentMethod);
  const countryCodes = [
    { code: '+54', country: 'AR', label: 'Argentina' },
    { code: '+591', country: 'BO', label: 'Bolivia' },
    { code: '+56', country: 'CL', label: 'Chile' },
    { code: '+57', country: 'CO', label: 'Colombia' },
    { code: '+506', country: 'CR', label: 'Costa Rica' },
    { code: '+593', country: 'EC', label: 'Ecuador' },
    { code: '+503', country: 'SV', label: 'El Salvador' },
    { code: '+34', country: 'ES', label: 'España' },
    { code: '+1', country: 'US', label: 'Estados Unidos' },
    { code: '+502', country: 'GT', label: 'Guatemala' },
    { code: '+504', country: 'HN', label: 'Honduras' },
    { code: '+52', country: 'MX', label: 'México' },
    { code: '+505', country: 'NI', label: 'Nicaragua' },
    { code: '+507', country: 'PA', label: 'Panamá' },
    { code: '+595', country: 'PY', label: 'Paraguay' },
    { code: '+51', country: 'PE', label: 'Perú' },
    { code: '+598', country: 'UY', label: 'Uruguay' },
    { code: '+58', country: 'VE', label: 'Venezuela' },
  ];
  const [countryCode, setCountryCode] = useState('+503');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    whatsapp: '',
    paymentProof: null
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'El nombre es requerido';
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = 'El nombre debe tener al menos 3 caracteres';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ingresa un correo electrónico válido';
    }

    if (!formData.whatsapp.trim()) {
      newErrors.whatsapp = 'El número de WhatsApp es requerido';
    } else if (!/^[\d\s+()-]{8,}$/.test(formData.whatsapp)) {
      newErrors.whatsapp = 'Ingresa un número de teléfono válido';
    }

    if (selectedMethod?.requiresProof && !formData.paymentProof) {
      newErrors.paymentProof = 'Debes adjuntar el comprobante de transferencia';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, paymentProof: 'Solo se permiten archivos de imagen' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, paymentProof: 'La imagen no debe superar 5MB' }));
        return;
      }
      setFormData(prev => ({ ...prev, paymentProof: file }));
      setPreviewUrl(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, paymentProof: null }));
    }
  };

  const removeFile = () => {
    setFormData(prev => ({ ...prev, paymentProof: null }));
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('event_id', event.id);
      fd.append('full_name', formData.fullName);
      fd.append('email', formData.email);
      fd.append('whatsapp', formData.whatsapp);
      fd.append('country_code', countryCode);
      fd.append('payment_method_id', paymentMethod);
      chairs.forEach(c => fd.append('chair_ids', c.id));
      if (formData.paymentProof) {
        fd.append('payment_proof', formData.paymentProof);
      }

      const result = await createReservation(fd);
      // If payment provider returned a gateway URL (e.g. Cubo), redirect there
      if (result?.paymentGatewayURL) {
        window.location.href = result.paymentGatewayURL;
        return;
      }
      onSuccess();
    } catch (err) {
      setErrors({ submit: err.message || 'Error al crear la reservación' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Summary */}
      <div className="p-3 bg-secondary rounded-lg border border-border space-y-2">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs text-muted-foreground">Sillas seleccionadas</p>
            <p className="text-sm font-medium text-foreground">{chairs.length} silla{chairs.length > 1 ? 's' : ''}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Total a pagar</p>
            <p className="text-lg font-bold text-primary">
              {formatPrice(totalPrice, event?.currency)}
            </p>
          </div>
        </div>
        <div className="space-y-0.5 max-h-20 overflow-y-auto">
          {chairs.map(chair => {
            const table = tables.find(t => t.id === chair.tableId);
            return (
              <div key={chair.id} className="flex justify-between text-xs text-muted-foreground">
                <span>{table?.label} · {chair.label}</span>
                <span>{formatPrice(chair.price, event?.currency)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-3">
        {/* Full Name */}
        <div className="space-y-1">
          <Label htmlFor="fullName" className="text-foreground text-sm">
            Nombre Completo <span className="text-primary">*</span>
          </Label>
          <Input
            id="fullName"
            name="fullName"
            data-testid="input-fullname"
            placeholder="Ej: Juan Pérez"
            value={formData.fullName}
            onChange={handleChange}
            className={`bg-secondary border-border focus:border-primary h-9 ${errors.fullName ? 'border-destructive' : ''}`}
          />
          {errors.fullName && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.fullName}
            </p>
          )}
        </div>

        {/* Email */}
        <div className="space-y-1">
          <Label htmlFor="email" className="text-foreground text-sm">
            Correo Electrónico <span className="text-primary">*</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            data-testid="input-email"
            placeholder="ejemplo@correo.com"
            value={formData.email}
            onChange={handleChange}
            className={`bg-secondary border-border focus:border-primary h-9 ${errors.email ? 'border-destructive' : ''}`}
          />
          {errors.email && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.email}
            </p>
          )}
        </div>

        {/* WhatsApp */}
        <div className="space-y-1">
          <Label htmlFor="whatsapp" className="text-foreground text-sm">
            Número de WhatsApp <span className="text-primary">*</span>
          </Label>
          <div className="flex gap-2">
            <Select value={countryCode} onValueChange={(val) => setCountryCode(val)}>
              <SelectTrigger data-testid="country-code-select" className="bg-secondary border-border h-9 w-[110px] shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {countryCodes.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.country} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="whatsapp"
              name="whatsapp"
              type="tel"
              data-testid="input-whatsapp"
              placeholder="7890 1234"
              value={formData.whatsapp}
              onChange={handleChange}
              className={`bg-secondary border-border focus:border-primary h-9 flex-1 ${errors.whatsapp ? 'border-destructive' : ''}`}
            />
          </div>
          {errors.whatsapp && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.whatsapp}
            </p>
          )}
        </div>

        {/* Payment Method Dropdown */}
        <div className="space-y-1">
          <Label className="text-foreground text-sm">
            Método de Pago <span className="text-primary">*</span>
          </Label>
          <Select
            value={paymentMethod}
            onValueChange={(value) => setPaymentMethod(value)}
          >
            <SelectTrigger data-testid="payment-method-select" className="bg-secondary border-border h-9">
              <SelectValue placeholder="Selecciona un método de pago" />
            </SelectTrigger>
            <SelectContent>
              {activeMethods.map((method) => (
                <SelectItem key={method.id} value={method.id.toString()}>
                  {method.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Payment method description */}
      {selectedMethod?.description && (
        <div className="mt-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-xs text-muted-foreground leading-relaxed">
            {selectedMethod.description}
          </p>
        </div>
      )}

      {/* Bank info + Upload (only for methods that require proof) */}
      {selectedMethod?.requiresProof && (
        <div className="mt-2 space-y-5">
          {selectedMethod.bankInfo && (
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <h4 className="text-xs font-medium text-foreground mb-1.5">Datos para Transferencia</h4>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <p>Banco: <span className="text-foreground">{selectedMethod.bankInfo.banco}</span></p>
                <p>Cuenta: <span className="text-foreground">{selectedMethod.bankInfo.cuenta}</span></p>
                <p>CLABE: <span className="text-foreground">{selectedMethod.bankInfo.clabe}</span></p>
                <p>Titular: <span className="text-foreground">{selectedMethod.bankInfo.titular}</span></p>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-foreground text-sm">
              Comprobante de Transferencia <span className="text-primary">*</span>
            </Label>
            
            {!previewUrl ? (
              <div
                data-testid="upload-area"
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors
                  ${errors.paymentProof 
                    ? 'border-destructive bg-destructive/5' 
                    : 'border-border hover:border-primary/50 hover:bg-primary/5'
                  }`}
              >
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-xs text-muted-foreground mb-0.5">
                  Haz clic para subir la imagen
                </p>
                <p className="text-xs text-muted-foreground opacity-60">
                  PNG, JPG hasta 5MB
                </p>
              </div>
            ) : (
              <div 
                data-testid="preview-container"
                className="relative rounded-lg overflow-hidden border border-border"
              >
                <img
                  src={previewUrl}
                  alt="Comprobante de pago"
                  className="w-full h-36 object-cover"
                />
                <button
                  type="button"
                  data-testid="remove-image-btn"
                  onClick={removeFile}
                  className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full hover:bg-black/80 transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-white" />
                </button>
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded text-xs text-white flex items-center gap-1">
                  <Image className="w-3 h-3" />
                  {formData.paymentProof?.name}
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              data-testid="file-input"
              onChange={handleFileChange}
              className="hidden"
            />

            {errors.paymentProof && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.paymentProof}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {errors.submit && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {errors.submit}
          </p>
        </div>
      )}
      <div className="flex gap-3 pt-1">
        <Button
          type="button"
          variant="outline"
          data-testid="cancel-reservation-btn"
          onClick={onCancel}
          className="flex-1 border-border hover:border-primary/50 h-9"
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          data-testid="submit-reservation-btn"
          className="flex-1 bg-primary hover:bg-accent glow-orange h-9"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Procesando...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Confirmar Reservación
            </>
          )}
        </Button>
      </div>

      <p className="text-xs text-center text-muted-foreground pb-1">
        Al confirmar, aceptas nuestros términos y condiciones de reservación.
      </p>
    </form>
  );
};

export default ReservationForm;
